import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Lock } from 'lucide-react';
import Footer from '../components/Footer';

export default function PrivacyPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#161616] text-white flex flex-col">
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-[#161616]/80 border-b border-white/5">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="text-xl sm:text-2xl font-bold tracking-tight cursor-pointer" style={{ fontFamily: 'Clash Display' }} data-testid="privacy-logo-btn">
            Move<span className="text-[#C6A97C]">More</span>
          </button>
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white transition" data-testid="privacy-home-btn">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </button>
        </div>
      </nav>

      <section className="pt-32 pb-16 px-6 flex-1">
        <div className="container mx-auto max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-full bg-[#D9C7A3] flex items-center justify-center">
                <Lock className="w-7 h-7 text-[#0B0B0B]" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#C6A97C] mb-1">YOUR DATA</div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ fontFamily: 'Clash Display' }}>Privacy Policy</h1>
              </div>
            </div>

            <p className="text-gray-300 text-lg leading-relaxed mb-10">
              MoveMore (operated by Burke Biokinetics) respects your privacy. This policy explains what we collect, why, and what rights you have under the South African Protection of Personal Information Act (POPIA) and the EU GDPR where applicable.
            </p>

            <div className="space-y-8 text-gray-300 leading-relaxed">
              <Section title="1. What we collect">
                <ul className="space-y-2 list-disc pl-5 text-gray-300">
                  <li><strong className="text-white">Account info</strong>: name, email, password (hashed), role.</li>
                  <li><strong className="text-white">Payment info</strong>: handled and stored by Yoco — we never see or store full card numbers.</li>
                  <li><strong className="text-white">Usage data</strong>: courses purchased, exercises completed, progress logs, messages you send.</li>
                  <li><strong className="text-white">Technical data</strong>: IP address, browser type, device — used for security and analytics.</li>
                </ul>
              </Section>

              <Section title="2. How we use it">
                <ul className="space-y-2 list-disc pl-5 text-gray-300">
                  <li>To deliver the service you paid for (programmes, videos, support).</li>
                  <li>To process payments and send you receipts.</li>
                  <li>To respond when you contact us.</li>
                  <li>To improve the platform — anonymised, aggregated usage data only.</li>
                  <li>To meet legal and tax obligations.</li>
                </ul>
              </Section>

              <Section title="3. Who we share it with">
                We share data only with the third parties strictly required to run the service:
                <ul className="space-y-2 list-disc pl-5 text-gray-300 mt-3">
                  <li><strong className="text-white">Yoco</strong> — payment processing.</li>
                  <li><strong className="text-white">Resend</strong> — sending you receipts and transactional email.</li>
                  <li><strong className="text-white">Hosting / database providers</strong> — to store your account and content securely.</li>
                </ul>
                We never sell your data to anyone.
              </Section>

              <Section title="4. How long we keep it">
                Account and purchase records are retained while your account is active and for up to 5 years afterwards (to meet South African tax and consumer-protection law). You can request earlier deletion (see Your Rights below) — we'll comply unless we're legally required to retain specific records.
              </Section>

              <Section title="5. Your rights">
                Under POPIA / GDPR you can:
                <ul className="space-y-2 list-disc pl-5 text-gray-300 mt-3">
                  <li>Ask what data we hold on you.</li>
                  <li>Correct anything that's wrong.</li>
                  <li>Request deletion of your account and data.</li>
                  <li>Withdraw consent at any time (though this may end your access).</li>
                  <li>Lodge a complaint with the South African Information Regulator.</li>
                </ul>
                Email <a href="mailto:brandon@burkebiokinetics.co.za" className="text-[#C6A97C] hover:underline">brandon@burkebiokinetics.co.za</a> to exercise any of these rights.
              </Section>

              <Section title="6. Cookies">
                We use a minimum of essential cookies — your login session token (kept in your browser's local storage). We don't use advertising or cross-site tracking cookies.
              </Section>

              <Section title="7. Security">
                Passwords are hashed with bcrypt. Connections are encrypted (HTTPS/TLS). We restrict admin access on a need-to-know basis. No system is 100% secure though, so we recommend you use a strong, unique password.
              </Section>

              <Section title="8. Children">
                MoveMore is not intended for users under 18. If you believe a minor has registered without parental consent, contact us and we'll delete the account.
              </Section>

              <Section title="9. Changes">
                We may update this policy occasionally. Significant changes will be communicated via email or an in-app banner before they take effect.
              </Section>

              <Section title="10. Contact">
                Privacy questions or data requests: <a href="mailto:brandon@burkebiokinetics.co.za" className="text-[#C6A97C] hover:underline">brandon@burkebiokinetics.co.za</a>.
              </Section>
            </div>

            <div className="mt-12 pt-8 border-t border-white/10 text-sm text-gray-500">
              <p>Burke Biokinetics &middot; 76 Reagent Road, Sea Point</p>
              <p className="mt-1">Last updated: April 2026</p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold mb-3 text-white" style={{ fontFamily: 'Clash Display' }}>{title}</h2>
      <div className="text-gray-300">{children}</div>
    </div>
  );
}
